import compact from 'lodash/compact';
import noop from 'lodash/noop';
import { useMemo } from 'react';

import {
   TopicCategory,
   VisitDocumentTemplateMetadata,
   VisitDocumentTemplateMetadataQuery,
   VisitDocumentTemplateType,
} from '@dreryk/egabinet-visit-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { Ordering } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   JustifyContent,
   StyledFlex,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';
import { Slot, TextPattern } from '@dreryk/watermelon';

import { documentTemplateActionTranslationKeys } from '../../../../../translations';

import { DocumentTemplateFillButton } from './cells/DocumentTemplateFillButton';
import { DocumentTemplateFinishButton } from './cells/DocumentTemplateFinishButton';
import { DocumentTemplateLetterSeparator } from './cells/DocumentTemplateLetterSeparator';
import { DocumentTemplateMarkAsFavoriteIcon } from './cells/DocumentTemplateMarkAsFavoriteIcon';
import { DocumentTemplateNameCell } from './cells/DocumentTemplateNameCell';
import { DocumentTemplateUnmarkAsFavoriteIcon } from './cells/DocumentTemplateUnmarkAsFavoriteIcon';
import { resolveLetterSeparator } from './DocumentTemplateTable.utils';

const DEFAULT_ORDERINGS: Ordering<VisitDocumentTemplateMetadata>[] = [
   {
      descending: true,
      property: 'favorite',
   },
   {
      descending: false,
      property: 'name',
   },
];

const SEARCH_ORDERINGS: Ordering<VisitDocumentTemplateMetadata>[] = [
   {
      descending: false,
      property: 'name',
   },
];

export type DocumentTemplateColumnsRenderOptions = {
   currentPageData: VisitDocumentTemplateMetadata[];
   showLetterSeparators: boolean;
};

type UseDocumentTemplateDataConfigurationOptions = {
   visitId: number;
   searchText: string | null;
   topicCategory: TopicCategory | null;
   favorite: boolean;
   priorityTemplateTypes: VisitDocumentTemplateType[] | undefined;
   priorityTemplateIds: number[] | undefined;
};

export const useDocumentTemplateDataConfiguration = (
   options: UseDocumentTemplateDataConfigurationOptions
): LazyLoadedDataConfigurationByCqrsQuery<VisitDocumentTemplateMetadata> => {
   const orderings = options.searchText ? SEARCH_ORDERINGS : DEFAULT_ORDERINGS;

   return useLazyLoadedDataConfigurationByCqrsQuery<VisitDocumentTemplateMetadata>(() => {
      const query = new VisitDocumentTemplateMetadataQuery();
      query.visitId = options.visitId;
      if (options.searchText) {
         query.name = TextPattern.anywhereIgnoreCase(options.searchText);
      }
      if (options.topicCategory) {
         query.topicCategories = Slot.of([options.topicCategory]);
      }
      if (options.favorite) {
         query.favorite = Slot.of(true);
      }
      query.sortingPriorityTemplateTypes = options.priorityTemplateTypes;
      query.sortingPriorityTemplateIds = options.priorityTemplateIds;
      return query;
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, orderings);
};

type UseDocumentTemplateColumnsOptions = {
   visitId: number;
   onDocumentTemplateOpen: VoidFunction;
   onFavoriteChange: VoidFunction;
   priorityTemplateTypes: VisitDocumentTemplateType[] | undefined;
   priorityTemplateIds: number[] | undefined;
};

// eslint-disable-next-line max-lines-per-function
export const useDocumentTemplateColumns = (
   options: UseDocumentTemplateColumnsOptions
): TableColumnConfiguration<VisitDocumentTemplateMetadata, any, DocumentTemplateColumnsRenderOptions>[] => {
   const prioritySet = useMemo(
      () =>
         new Set<string | number | undefined>([
            ...compact(options.priorityTemplateIds),
            ...compact(options.priorityTemplateTypes),
         ]),
      [options.priorityTemplateIds, options.priorityTemplateTypes]
   );

   // eslint-disable-next-line max-lines-per-function
   return useMemo((): TableColumnConfiguration<
      VisitDocumentTemplateMetadata,
      any,
      DocumentTemplateColumnsRenderOptions
   >[] => {
      const columnsWidth = createDynamicColumnsWidth({
         name: '100%',
         options: '152px',
      });

      return [
         {
            name: 'name',
            label: documentTemplateActionTranslationKeys.form.table.headers.name,
            width: columnsWidth.name,
            accessor: noop,
            renderer: ({
               bean,
               index,
               options: renderOptions,
            }: ValueRendererProps<VisitDocumentTemplateMetadata, void, DocumentTemplateColumnsRenderOptions>) => {
               const letterSeparator = renderOptions.showLetterSeparators
                  ? resolveLetterSeparator(renderOptions.currentPageData, index)
                  : null;

               return (
                  <StyledFlex direction={'column'} width={'100%'}>
                     {letterSeparator ? <DocumentTemplateLetterSeparator letter={letterSeparator} /> : null}
                     <DocumentTemplateNameCell name={bean.name} />
                  </StyledFlex>
               );
            },
         },
         {
            width: columnsWidth.options,
            justifyContent: JustifyContent.END,
            accessor: noop,
            name: 'options',
            sortingDisabled: true,
            renderer: ({
               bean,
               index,
               options: renderOptions,
            }: ValueRendererProps<VisitDocumentTemplateMetadata, void, DocumentTemplateColumnsRenderOptions>) => {
               const hasSeparator =
                  renderOptions.showLetterSeparators &&
                  resolveLetterSeparator(renderOptions.currentPageData, index) !== null;

               const favoriteIcon = bean.favorite ? (
                  <DocumentTemplateUnmarkAsFavoriteIcon
                     onUnarkAsFavorite={options.onFavoriteChange}
                     printTemplateId={bean.printTemplateId}
                     type={bean.type}
                  />
               ) : (
                  <DocumentTemplateMarkAsFavoriteIcon
                     onMarkAsFavorite={options.onFavoriteChange}
                     printTemplateId={bean.printTemplateId}
                     type={bean.type}
                  />
               );

               const button =
                  prioritySet.has(bean.printTemplateId) || prioritySet.has(bean.type) ? (
                     <DocumentTemplateFinishButton
                        name={bean.name}
                        type={bean.type}
                        printTemplateId={bean.printTemplateId}
                        visitId={options.visitId}
                        onDocumentTemplateOpen={options.onDocumentTemplateOpen}
                     />
                  ) : (
                     <DocumentTemplateFillButton
                        name={bean.name}
                        type={bean.type}
                        printTemplateId={bean.printTemplateId}
                        visitId={options.visitId}
                        onDocumentTemplateOpen={options.onDocumentTemplateOpen}
                     />
                  );
               return (
                  <StyledFlex direction={'column'} width={'100%'}>
                     {hasSeparator ? <DocumentTemplateLetterSeparator letter={'\u00A0'} asSpacer /> : null}
                     <StyledFlex alignItems={'center'} justifyContent={'flex-end'} gap={8}>
                        {favoriteIcon}
                        {button}
                     </StyledFlex>
                  </StyledFlex>
               );
            },
         },
      ];
   }, [options.onFavoriteChange, options.visitId, options.onDocumentTemplateOpen, prioritySet]);
};
